<script lang="ts">
	import { getContext } from 'svelte';
	import { writable, type Writable } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import { Text, Alert, Token, Toggle } from '@Shelly/shelly-design-system';
	import { is_device_action_ui } from 'src/types/DeviceActionUI';
	import { scrollToElementInWizard } from 'src/components/Lists/list-helpers';
	import { capFirstChar, conditionsFunc } from '../../../../../../../../../helpers/utilities';
	import { DO_WIZARD_STEPS_INDEXES } from '../../../helpers/do-wizard-helpers';
	import CustomDynamicInput from 'src/components/CustomDynamicInput.svelte';
	import type { TimerUI } from 'src/types/DeviceActionUI';
	// export let timer: TimerUI;
	export let showToggleWarning: boolean = false;
	export let sceneData: any;
	export let active_actions = [];

	// Open the section by default when the edited action already carries a timer,
	// so the toggle reflects the saved state instead of always starting collapsed.
	// Falls back to the raw translated action to stay correct regardless of the
	// order in which the wizard steps initialise `deviceAction.timer`.
	let showTimerCustomSettings =
		Number(sceneData?.deviceAction?.timer?.value) > 0 ||
		Number(sceneData?.translated?.timer?.value) > 0;
	let additionContentElRef: HTMLElement = null;

	const defaultTimer: TimerUI = {
		units: 'sec',
		value: 0,
	};

	if (is_device_action_ui(sceneData.translated)) {
		if (sceneData.translated.timer !== undefined) {
			sceneData.timer = { ...sceneData.translated.timer };
		}
	}

	$: showToggleWarning =
		// $devices[selectedDevice.value]?.gen === 1 &&
		active_actions[0]?.value === 'toggle';

	// Reset timer only when toggle warning applies — never spread-assign on every tick (Svelte 5 loop).
	$: if (showToggleWarning) {
		const timer = sceneData.deviceAction.timer;
		if (timer.value !== defaultTimer.value || timer.units !== defaultTimer.units) {
			sceneData.deviceAction.timer = { ...defaultTimer };
		}
	}

	const timerTimeTypes = [
		{
			title: capFirstChar($format('select_units_seconds')),
			value: 'sec',
		},
		{
			title: capFirstChar($format('select_units_minutes')),
			value: 'min',
		},
	];

	let onUpdateTimerValue = (type: 'add' | 'substract' = 'add') => {
		const currentTimerValue = +sceneData.deviceAction.timer.value;
		if (sceneData.deviceAction.actions.length <= 0) {
			sceneData.isValid = false;
		} else {
			sceneData.isValid = true;
		}
		sceneData.deviceAction.timer.value =
			type === 'substract'
				? !error && currentTimerValue > 0
					? currentTimerValue - 1
					: 0
				: !error
					? currentTimerValue + 1
					: 0;
	};

	const addActionContainersRefs: Writable<{
		wizardContainerRef: HTMLElement | null;
		scrollContainerRef: HTMLElement | null;
	}> =
		getContext('addActionContainersRefs') ??
		writable({ wizardContainerRef: null, scrollContainerRef: null });

	$: if (
		additionContentElRef &&
		$addActionContainersRefs?.wizardContainerRef &&
		$addActionContainersRefs?.scrollContainerRef
	) {
		scrollToElementInWizard(
			$addActionContainersRefs.wizardContainerRef,
			additionContentElRef,
			$addActionContainersRefs.scrollContainerRef,
		);
	}
	let error;
	$: timerHasInputError = conditionsFunc(sceneData.deviceAction.timer.value, false, true);
	$: error = timerHasInputError
		? $format('value_must_be_valid_equal_or_greater_than_zero', {
				default: 'Value must be a valid number and equal or greater than 0.',
			})
		: undefined;

	$: if (timerHasInputError && !showTimerCustomSettings) {
		if (sceneData.deviceAction.timer.value !== 0) {
			sceneData.deviceAction.timer.value = 0;
		}
		if (sceneData.isValid !== true) {
			sceneData.isValid = true;
		}
	}

	let inputHandler = (e) => {
		sceneData.validStepIndex = DO_WIZARD_STEPS_INDEXES.CHANGE_STATE;

		if (conditionsFunc(e.target.value, false, true) || sceneData.deviceAction.actions.length <= 0) {
			sceneData.isValid = false;
		} else {
			sceneData.isValid = true;
		}
		sceneData.deviceAction.timer.value = e.target.value;
	};

	let minusFunction = () => onUpdateTimerValue('substract');

	/**
	 * Toggles the custom timer section. When it is switched off we discard any
	 * value the user already typed and restore the default timer, so a hidden
	 * leftover value can never be serialized into the saved scene action.
	 */
	const toggleTimerCustomSettings = () => {
		showTimerCustomSettings = !showTimerCustomSettings;

		if (!showTimerCustomSettings) {
			sceneData.deviceAction.timer = { ...defaultTimer };
			sceneData.isValid = true;
		}
	};
</script>

<Toggle
	checked={showTimerCustomSettings}
	on:click={toggleTimerCustomSettings}
	class="mt-m mb-s"
	size="m"
	id="toggle-p9of351"
>
	{$format('title_timer_custom_settings', { default: 'Timer custom settings' })}
</Toggle>

<Alert
	class="mb-s"
	type="info"
	heading={$format('title_timer_custom_settings', {
		default: 'Timer custom settings',
	})}
	message={$format('alert_message_timer_custom_settings', {
		default:
			'Changes the state of the device for a period of time of your choice and then returns it to the previous state.',
	})}
/>

{#if showTimerCustomSettings}
	<div bind:this={additionContentElRef}>
		<Text element="h2" class="mb-s">
			{capFirstChar($format('collapse_title_timer', { default: 'Timer' }))}
		</Text>

		{#if showToggleWarning}
			<div class="mb-s">
				<Alert
					type="warning"
					heading=""
					message={$format('gen1_alert_toggle_with_timer', {
						default: 'This device does not support command "toggle" with timer.',
					})}
				/>
			</div>
		{/if}

		<Text styleType="body-small" class="mb-s">
			{capFirstChar(
				$format('turn_with_timer', {
					default: 'Change the device state for {value_and_units}',
					values: {
						value_and_units: $format(
							`delay_units_${sceneData.deviceAction.timer.units === 'sec' ? 'seconds' : 'minutes'}`,
							{
								values: { n: error ? 0 : sceneData.deviceAction.timer.value },
							},
						),
					},
				}),
			)}
		</Text>

		<div class="d-flex flex-wrap mt-s">
			<CustomDynamicInput
				additionalContainerClass="mb-sm-s"
				additionalInputAttributes={{
					min: 0,
					max: 1440,
					icon: 'fas fa-clock',
					disabled: showToggleWarning,
				}}
				additionalButtonAttributes={{
					disabled: showToggleWarning,
				}}
				disabledMinusBtn={+sceneData.deviceAction.timer.value <= 0}
				bind:value={sceneData.deviceAction.timer.value}
				bind:error
				bind:inputHandler
				bind:minusFunction
				bind:plusFunction={onUpdateTimerValue}
			/>

			<div class="d-flex align-items-start">
				{#each timerTimeTypes as timeType}
					<Token
						id="token-wpefwge"
						class="ml-s"
						disabled={showToggleWarning}
						title={timeType.title}
						selected={sceneData.deviceAction.timer.units === timeType.value}
						value={timeType.value}
						size="m"
						on:click={() => {
							sceneData.deviceAction.timer.units = timeType.value;
						}}
					/>
				{/each}
			</div>
		</div>
	</div>
{/if}
